/**
 * Responsibility: get PSA's population counts onto a card, by whichever of
 * the two routes is available.
 *
 * The gem rate is what turns "a PSA 10 is worth six times raw" into "and one
 * in eight will be a PSA 10", which is the difference between the best case
 * and the average case. So this dialog exists to make sure that number can
 * always be obtained, even when a credential cannot be.
 *
 * Two routes, and the spec ID wins when both are filled in. A spec ID gets
 * counts that stay current on every refresh; typed counts are a snapshot that
 * silently ages, and a population only ever grows - so a stale count
 * overstates scarcity, which is the direction that loses money. The dialog
 * says as much rather than resolving the conflict quietly.
 *
 * Total is accepted separately from the per-grade counts because PSA's report
 * includes grades 1 through 8, plus authentic-only submissions, that this
 * model does not reason about individually but which do belong in the
 * denominator. Leaving it blank falls back to the sum of the tens and nines,
 * which overstates the gem rate.
 */
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Opportunity, PopulationInput } from '../../api/client';

type PopulationDialogProps = {
  row: Opportunity | null;
  pending?: boolean;
  /** Whether a PSA credential exists, which decides if a spec ID does anything. */
  populationAvailable: boolean;
  onClose: () => void;
  onSubmitCounts: (id: string, population: PopulationInput) => void;
  onSubmitSpecId: (id: string, specId: string) => void;
};

/** Empty means zero here; a blank count is not a missing count. */
function count(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function PopulationDialog({
  row,
  pending = false,
  populationAvailable,
  onClose,
  onSubmitCounts,
  onSubmitSpecId,
}: PopulationDialogProps) {
  const [specId, setSpecId] = useState('');
  const [tens, setTens] = useState('');
  const [nines, setNines] = useState('');
  const [total, setTotal] = useState('');

  // Seeded from whatever is already recorded, so updating a count is an edit
  // rather than a re-entry of all three.
  useEffect(() => {
    if (!row) return;
    setSpecId(row.psa_spec_id ?? '');
    setTens(row.population ? String(row.population.cert_count_10) : '');
    setNines(row.population ? String(row.population.cert_count_9) : '');
    setTotal(row.population ? String(row.population.graded_total) : '');
  }, [row]);

  if (!row) return null;

  const specChanged = specId.trim() !== (row.psa_spec_id ?? '');
  const willFetch = populationAvailable && specId.trim().length > 0 && specChanged;

  const submit = () => {
    if (willFetch) {
      onSubmitSpecId(row.id, specId.trim());
      return;
    }
    // Spec ID still saved when it changed but cannot be used yet, so it is
    // ready the moment a credential appears.
    if (specChanged) {
      onSubmitSpecId(row.id, specId.trim());
      return;
    }
    onSubmitCounts(row.id, {
      cert_count_10: count(tens),
      cert_count_9: count(nines),
      cert_count_total: count(total),
    });
  };

  const impliedTotal = count(total) || count(tens) + count(nines);
  const impliedRate = impliedTotal > 0 ? (count(tens) / impliedTotal) * 100 : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Population for {row.card_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="PSA spec ID"
            value={specId}
            onChange={(event) => setSpecId(event.target.value)}
            autoFocus
            fullWidth
            helperText={
              populationAvailable
                ? 'The counts are fetched from PSA and refreshed every time this card is re-priced.'
                : 'Saved, but not usable until a PSA credential is configured.'
            }
          />

          <Typography variant="caption" color="text.secondary">
            Find it in the URL of this card&apos;s{' '}
            <Link
              href="https://www.psacard.com/pop"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              PSA population report
            </Link>
            .
          </Typography>

          {willFetch ? (
            <Alert severity="info">
              Saving will fetch the counts from PSA, so the fields below are ignored.
            </Alert>
          ) : null}

          <Divider>
            <Typography variant="caption" color="text.secondary">
              or enter them by hand
            </Typography>
          </Divider>

          <TextField
            label="PSA 10s"
            value={tens}
            onChange={(event) => setTens(event.target.value)}
            type="number"
            disabled={willFetch}
            fullWidth
          />
          <TextField
            label="PSA 9s"
            value={nines}
            onChange={(event) => setNines(event.target.value)}
            type="number"
            disabled={willFetch}
            helperText="Clean nines only, not the ones with a qualifier."
            fullWidth
          />
          <TextField
            label="Total graded"
            value={total}
            onChange={(event) => setTotal(event.target.value)}
            type="number"
            disabled={willFetch}
            helperText="Every grade, not just 9s and 10s. Leaving this out overstates the gem rate."
            fullWidth
          />

          {impliedRate === null || willFetch ? null : (
            <Alert severity={impliedRate < 15 ? 'warning' : 'info'}>
              That is a gem rate of {impliedRate.toFixed(1)}%.
              {impliedRate < 15
                ? ' Hard to gem, so the average outcome will be a good deal worse than the best case.'
                : ''}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={pending}>
          {pending ? 'Saving' : willFetch ? 'Save and fetch' : 'Save counts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
